import React, { useEffect, useState } from "react";
import { IDS } from "@/constants/testIds";
import { ArrowUpRight, ChevronRight, Circle } from "lucide-react";

const lines = [
  { p: "root@aetheria:~$", c: "./init --services solana,rust,protocol" },
  { p: "[boot]", c: "loading modules ............ ok", dim: true },
  { p: "[boot]", c: "validators: 12 / 12 online", dim: true },
];

const Hero = () => {
  const [typed, setTyped] = useState("");
  const target = "./deploy --to mainnet --confirm";

  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTyped(target.slice(0, i));
      if (i >= target.length) clearInterval(id);
    }, 55);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      id="top"
      data-testid={IDS.hero}
      className="relative min-h-[88vh] flex items-center"
    >
      <div className="mx-auto max-w-[1280px] w-full px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-12 gap-10">
        {/* LEFT: text */}
        <div className="lg:col-span-7">
          <div
            data-testid={IDS.heroStatusOnline}
            className="inline-flex items-center gap-2 border border-[rgba(0,255,65,0.4)] px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-[#00FF41] bg-[rgba(0,255,65,0.05)]"
          >
            <Circle size={6} fill="#00FF41" stroke="#00FF41" />
            <span>online · accepting q1 build slots</span>
          </div>

          <h1
            data-testid={IDS.heroTitle}
            className="mt-8 font-mono font-bold tracking-tighter text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] text-white"
          >
            We ship{" "}
            <span className="text-[#00FF41] glow-text">production-grade</span>{" "}
            blockchain
            <br />
            infrastructure.
            <span className="cursor-block" />
          </h1>

          <p className="mt-7 max-w-xl text-base text-zinc-400 leading-relaxed">
            aetheria is a senior engineering studio building on{" "}
            <span className="text-white">Solana</span>,{" "}
            <span className="text-white">Rust</span>, and EVM L2s. From token
            launches to validator fleets — we operate where the bytecode meets
            the metal.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contact"
              data-testid={IDS.heroCtaPrimary}
              className="btn-term"
            >
              [ Start a Project ]
              <ArrowUpRight size={14} strokeWidth={1.75} />
            </a>
            <a
              href="#services"
              data-testid={IDS.heroCtaSecondary}
              className="btn-term"
              data-variant="ghost"
            >
              <ChevronRight size={14} strokeWidth={1.75} />
              See Services
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 max-w-md gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
            <div>
              <div className="text-[#00FF41] text-sm normal-case tracking-normal font-semibold">
                $1.2B+
              </div>
              <div>tvl secured</div>
            </div>
            <div>
              <div className="text-[#00FF41] text-sm normal-case tracking-normal font-semibold">
                42
              </div>
              <div>contracts shipped</div>
            </div>
            <div>
              <div className="text-[#00FF41] text-sm normal-case tracking-normal font-semibold">
                17
              </div>
              <div>protocols launched</div>
            </div>
          </div>
        </div>

        {/* RIGHT: terminal */}
        <div className="lg:col-span-5">
          <div
            data-testid={IDS.heroPrompt}
            className="pane relative scanlines"
          >
            {/* header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-[rgba(0,255,65,0.2)] bg-black/40">
              <div className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 bg-[#27272a] border border-[#3f3f46]" />
                <span className="w-2.5 h-2.5 bg-[#27272a] border border-[#3f3f46]" />
                <span className="w-2.5 h-2.5 bg-[#00FF41]/70 border border-[#00FF41]" />
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                aetheria — ssh session
              </div>
              <div className="text-[10px] text-zinc-600">tty.01</div>
            </div>

            <div className="p-5 text-[13px] leading-relaxed font-mono">
              {lines.map((l, i) => (
                <div
                  key={i}
                  className="term-in"
                  style={{ animationDelay: `${i * 220}ms` }}
                >
                  <span className="text-[#00FF41]">{l.p}</span>{" "}
                  <span className={l.dim ? "text-zinc-500" : "text-zinc-100"}>
                    {l.c}
                  </span>
                </div>
              ))}

              <div
                className="term-in mt-2"
                style={{ animationDelay: "700ms" }}
              >
                <span className="text-[#00FF41]">root@aetheria:~$</span>{" "}
                <span className="text-zinc-100">{typed}</span>
                <span className="text-[#00FF41] animate-pulse">█</span>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-px bg-[rgba(0,255,65,0.15)] border border-[rgba(0,255,65,0.15)]">
                {[
                  ["NETWORK", "solana · mainnet-beta"],
                  ["SLOT", "284_119_407"],
                  ["TPS", "3_217"],
                  ["VALIDATORS", "12 / 12 ✓"],
                ].map(([k, v]) => (
                  <div key={k} className="bg-[#0a0a0a] px-3 py-3">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-zinc-500">
                      {k}
                    </div>
                    <div className="mt-1 text-[12px] text-[#00FF41]">{v}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                // last sync: just now
              </div>
            </div>
          </div>

          {/* sub note */}
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
            // env: production · region: global
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
